import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { supabase } from '../../../supabaseClient';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 520,
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: 20,
  boxShadow: '0 24px 60px rgba(15, 23, 42, 0.08)',
  padding: '2rem',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.45rem',
  fontSize: '0.95rem',
  fontWeight: 600,
  color: '#0f172a',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.9rem 1rem',
  borderRadius: 12,
  border: '1px solid #cbd5e1',
  background: '#ffffff',
  color: '#0f172a',
  fontSize: '0.95rem',
  outline: 'none',
  boxSizing: 'border-box',
};

const errorTextStyle: React.CSSProperties = {
  color: '#b91c1c',
  fontSize: '0.82rem',
  marginTop: '0.45rem',
};

const dividerTextStyle: React.CSSProperties = {
  position: 'relative',
  textAlign: 'center',
  margin: '1.2rem 0',
  fontSize: '0.85rem',
  color: '#64748b',
};

const SignupPage: React.FC = () => {
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  const [fieldErrors, setFieldErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    password?: string;
    passwordConfirm?: string;
  }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [formMessage, setFormMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);

  const validate = () => {
    const errors: {
      firstName?: string;
      lastName?: string;
      email?: string;
      password?: string;
      passwordConfirm?: string;
    } = {};

    if (!firstName.trim()) {
      errors.firstName = 'First name is required.';
    }

    if (!lastName.trim()) {
      errors.lastName = 'Last name is required.';
    }

    if (!email.trim()) {
      errors.email = 'Email is required.';
    } else if (!EMAIL_REGEX.test(email.trim())) {
      errors.email = 'Enter a valid email address.';
    }

    if (!password) {
      errors.password = 'Password is required.';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters.';
    }

    if (!passwordConfirm) {
      errors.passwordConfirm = 'Please confirm your password.';
    } else if (passwordConfirm !== password) {
      errors.passwordConfirm = 'Passwords do not match.';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const clearTopLevelMessages = () => {
    if (formError) setFormError(null);
    if (formMessage) setFormMessage(null);
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormMessage(null);

    if (!validate()) return;

    try {
      setSubmitting(true);

      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            full_name: `${firstName.trim()} ${lastName.trim()}`,
            account_type: 'customer',
          },
        },
      });

      if (error) {
        setFormError(error.message);
        return;
      }

      if (data.session) {
        navigate('/dashboard', { replace: true });
        return;
      }

      setFormMessage(
        'Account created. Please check your email to confirm your account, then log in.'
      );
    } catch (err) {
      console.error('Signup error', err);
      setFormError('Something went wrong. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      clearTopLevelMessages();
      setGoogleSubmitting(true);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account',
          },
        },
      });

      if (error) {
        setFormError(error.message);
      }
    } catch (err) {
      console.error('Google signup error', err);
      setFormError('Google sign-up could not be started. Try again in a moment.');
    } finally {
      setGoogleSubmitting(false);
    }
  };

  return (
    <main
      style={{
        minHeight: 'calc(100vh - 160px)',
        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 40%, #f8fafc 100%)',
        padding: '3rem 1.25rem 4rem',
      }}
    >
      <div
        style={{
          maxWidth: 1120,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '2rem',
          alignItems: 'center',
        }}
      >
        <section>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.45rem 0.8rem',
              borderRadius: 999,
              background: '#e2e8f0',
              color: '#334155',
              fontSize: '0.82rem',
              fontWeight: 700,
              letterSpacing: '0.01em',
              marginBottom: '1rem',
            }}
          >
            HnB account
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: 'clamp(2rem, 4vw, 3.2rem)',
              lineHeight: 1.05,
              letterSpacing: '-0.03em',
              color: '#0f172a',
            }}
          >
            Create your HnB account
          </h1>

          <p
            style={{
              marginTop: '1rem',
              maxWidth: 580,
              fontSize: '1.02rem',
              lineHeight: 1.75,
              color: '#475569',
            }}
          >
            Register to save activity, continue AI preview journeys, manage bookings,
            and move from public discovery into authenticated customer features.
          </p>

          <div
            style={{
              marginTop: '1.5rem',
              display: 'grid',
              gap: '0.9rem',
              color: '#334155',
              fontSize: '0.95rem',
            }}
          >
            <div>• Save account-based activity and future booking history.</div>
            <div>• Continue from guest exploration into protected customer features.</div>
            <div>• Keep customer access separate from provider and staff areas.</div>
          </div>
        </section>

        <section>
          <div style={cardStyle}>
            <h2
              style={{
                margin: 0,
                fontSize: '1.5rem',
                color: '#0f172a',
                letterSpacing: '-0.02em',
              }}
            >
              Sign up
            </h2>

            <p
              style={{
                marginTop: '0.6rem',
                marginBottom: '1.5rem',
                color: '#64748b',
                lineHeight: 1.6,
                fontSize: '0.95rem',
              }}
            >
              Create a customer account to continue with HnB.
            </p>

            <button
              type="button"
              onClick={handleGoogleSignup}
              disabled={googleSubmitting || submitting}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                padding: '0.9rem 1rem',
                borderRadius: 999,
                border: '1px solid #d1d5db',
                background: '#ffffff',
                color: '#111827',
                fontSize: '0.95rem',
                fontWeight: 700,
                cursor: googleSubmitting || submitting ? 'not-allowed' : 'pointer',
                marginBottom: '1rem',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f3f4f6',
                  fontSize: '0.9rem',
                  fontWeight: 800,
                }}
              >
                G
              </span>
              {googleSubmitting ? 'Redirecting to Google...' : 'Continue with Google'}
            </button>

            <div style={dividerTextStyle}>
              <span
                style={{
                  background: '#ffffff',
                  padding: '0 0.75rem',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                or sign up with email
              </span>
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  height: 1,
                  background: '#e5e7eb',
                  transform: 'translateY(-50%)',
                }}
              />
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: '1rem',
                  marginBottom: '1rem',
                }}
              >
                <div>
                  <label htmlFor="firstName" style={labelStyle}>
                    First name
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      clearTopLevelMessages();
                      if (fieldErrors.firstName) {
                        setFieldErrors((prev) => ({ ...prev, firstName: undefined }));
                      }
                    }}
                    style={inputStyle}
                    aria-invalid={fieldErrors.firstName ? 'true' : 'false'}
                  />
                  {fieldErrors.firstName && (
                    <p style={errorTextStyle}>{fieldErrors.firstName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName" style={labelStyle}>
                    Last name
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      clearTopLevelMessages();
                      if (fieldErrors.lastName) {
                        setFieldErrors((prev) => ({ ...prev, lastName: undefined }));
                      }
                    }}
                    style={inputStyle}
                    aria-invalid={fieldErrors.lastName ? 'true' : 'false'}
                  />
                  {fieldErrors.lastName && (
                    <p style={errorTextStyle}>{fieldErrors.lastName}</p>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="email" style={labelStyle}>
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearTopLevelMessages();
                    if (fieldErrors.email) {
                      setFieldErrors((prev) => ({ ...prev, email: undefined }));
                    }
                  }}
                  style={inputStyle}
                  aria-invalid={fieldErrors.email ? 'true' : 'false'}
                />
                {fieldErrors.email && <p style={errorTextStyle}>{fieldErrors.email}</p>}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="password" style={labelStyle}>
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clearTopLevelMessages();
                    if (fieldErrors.password) {
                      setFieldErrors((prev) => ({ ...prev, password: undefined }));
                    }
                    if (fieldErrors.passwordConfirm && passwordConfirm) {
                      setFieldErrors((prev) => ({ ...prev, passwordConfirm: undefined }));
                    }
                  }}
                  style={inputStyle}
                  aria-invalid={fieldErrors.password ? 'true' : 'false'}
                />
                {fieldErrors.password && (
                  <p style={errorTextStyle}>{fieldErrors.password}</p>
                )}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="passwordConfirm" style={labelStyle}>
                  Confirm password
                </label>
                <input
                  id="passwordConfirm"
                  type="password"
                  autoComplete="new-password"
                  value={passwordConfirm}
                  onChange={(e) => {
                    setPasswordConfirm(e.target.value);
                    clearTopLevelMessages();
                    if (fieldErrors.passwordConfirm) {
                      setFieldErrors((prev) => ({ ...prev, passwordConfirm: undefined }));
                    }
                  }}
                  style={inputStyle}
                  aria-invalid={fieldErrors.passwordConfirm ? 'true' : 'false'}
                />
                {fieldErrors.passwordConfirm && (
                  <p style={errorTextStyle}>{fieldErrors.passwordConfirm}</p>
                )}
              </div>

              {formError && (
                <div
                  style={{
                    marginBottom: '1rem',
                    padding: '0.85rem 1rem',
                    borderRadius: 12,
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#991b1b',
                    fontSize: '0.92rem',
                    lineHeight: 1.6,
                  }}
                >
                  {formError}
                </div>
              )}

              {formMessage && (
                <div
                  style={{
                    marginBottom: '1rem',
                    padding: '0.85rem 1rem',
                    borderRadius: 12,
                    background: '#ecfdf5',
                    border: '1px solid #a7f3d0',
                    color: '#065f46',
                    fontSize: '0.92rem',
                    lineHeight: 1.6,
                  }}
                >
                  {formMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || googleSubmitting}
                style={{
                  width: '100%',
                  border: 'none',
                  borderRadius: 999,
                  padding: '0.95rem 1rem',
                  background: submitting ? '#475569' : '#111827',
                  color: '#ffffff',
                  fontSize: '0.96rem',
                  fontWeight: 700,
                  cursor: submitting || googleSubmitting ? 'not-allowed' : 'pointer',
                  boxShadow: '0 14px 30px rgba(15, 23, 42, 0.14)',
                }}
              >
                {submitting ? 'Creating account...' : 'Sign up'}
              </button>
            </form>

            <p
              style={{
                marginTop: '1rem',
                marginBottom: 0,
                fontSize: '0.92rem',
                color: '#64748b',
                lineHeight: 1.6,
              }}
            >
              Already have an account?{' '}
              <Link
                to="/login"
                style={{
                  color: '#0f172a',
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                Log in
              </Link>
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default SignupPage;